// Camera footprint for the minimap: the ground points under the four screen corners.
//
//   ┌───────────┐         tilted camera: the top corners look past the horizon, so each corner
//   │ 0       1 │         walks toward the screen centre until its ray meets the ground
//   │           │         (Footprint order: top-left, top-right, bottom-right, bottom-left)
//   │ 3       2 │
//   └───────────┘

import { signal, type ReadonlySignal } from '@preact/signals';
import { WORLD_W, worldToLonLat } from '../core/grid.ts';
import type { AppState, Services } from '../services/api.ts';
import type { Footprint } from '../ui/extensions.ts';
import type { CoreMapView } from '../view/index.ts';

/** Screen corners as fractions of the canvas, clockwise from top-left. */
const CORNERS: ReadonlyArray<readonly [number, number]> = [[0, 0], [1, 0], [1, 1], [0, 1]];

/** A missed corner moves this fraction of the way to the centre per try. */
const WALK_STEP = 0.1;
const MAX_WALKS = 9;
const CENTRE = 0.5;

/** Just inside the seam, which lonLatToWorld would wrap to the other edge. */
const LON_EDGE = 179.99;

/** The footprint is recomputed at most this often (ms) while the camera moves. */
const REFRESH_MS = 100;

/** Poses closer than this are the same footprint. */
const POSE_EPSILON = 1e-3;

interface Ground {
  /** World x before wrapping: a view wider than the world spans more than WORLD_W. */
  x: number;
  lon: number;
  lat: number;
}

function groundAt(view: CoreMapView, rect: DOMRect, fx: number, fy: number): Ground | null {
  for (let step = 0; step <= MAX_WALKS; step++) {
    const t = step * WALK_STEP;
    const x = rect.left + (fx + (CENTRE - fx) * t) * rect.width;
    const y = rect.top + (fy + (CENTRE - fy) * t) * rect.height;
    const hit = view.core.camera.groundAt(x, y);
    if (hit) {
      return { x: hit.x, ...worldToLonLat(hit.x, hit.z) };
    }
  }
  return null;
}

/** When the view is wider than the world, wrapped corners would collapse to a sliver: use the full width. */
function fullWidth(corners: Ground[]): Footprint {
  const xs = corners.map((c) => c.x);
  const centre = (Math.min(...xs) + Math.max(...xs)) / 2;
  return corners.map((c): [number, number] => [c.x < centre ? -LON_EDGE : LON_EDGE, c.lat]);
}

function compute(view: CoreMapView, canvas: HTMLElement): Footprint | null {
  const rect = canvas.getBoundingClientRect();
  const corners: Ground[] = [];
  for (const [fx, fy] of CORNERS) {
    const ground = groundAt(view, rect, fx, fy);
    if (!ground) {
      return null;
    }
    corners.push(ground);
  }

  const xs = corners.map((c) => c.x);
  if (Math.max(...xs) - Math.min(...xs) >= WORLD_W) {
    return fullWidth(corners);
  }
  return corners.map((c): [number, number] => [c.lon, c.lat]);
}

/** A signal of the camera footprint, refreshed after frames in which the camera moved. */
export function createFootprint(view: CoreMapView): ReadonlySignal<Footprint | null> {
  const footprint = signal<Footprint | null>(null);
  const canvas = view.core.engine.renderer.domElement;
  let lastAt = -Infinity;
  let last = { x: NaN, z: NaN, height: NaN, yaw: NaN, pitch: NaN };

  view.core.engine.afterFrame(() => {
    const now = performance.now();
    if (now - lastAt < REFRESH_MS) {
      return;
    }

    const pose = view.core.camera.pose();
    const moved = (Object.keys(last) as Array<keyof typeof last>).some((k) => !(Math.abs(pose[k] - last[k]) < POSE_EPSILON));
    if (!moved) {
      return;
    }

    lastAt = now;
    last = { x: pose.x, z: pose.z, height: pose.height, yaw: pose.yaw, pitch: pose.pitch };
    footprint.value = compute(view, canvas);
  });
  return footprint;
}

/** `services` with `state.footprint` added, for the minimap to feature-detect. */
export function withFootprint(services: Services, footprint: ReadonlySignal<Footprint | null>): Services {
  const state: AppState & { footprint: ReadonlySignal<Footprint | null> } = { ...services.state, footprint };
  return { ...services, state };
}
